import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  Button,
  Box,
  Avatar,
  Typography,
  Paper,
  Chip,
  Alert,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import api from '../../../utils/axiosConfig';
import AddressMapSelector from '../../Supplier/AddressMapSelector';
import { supplierCategories } from '../../../config/supplierTaxonomy';

const getVerificationStatus = ({ isVerified, status }) => {
  if (!isVerified) return { label: 'Not Verified', color: 'default' };
  if (status === 'active') return { label: 'Active', color: 'success' };
  return { label: 'Pending', color: 'warning' };
};

const SupplierProfile = ({ supplier, onUpdate }) => {
  const toCsv = (value) => (Array.isArray(value) ? value.join(', ') : '');
  const fromCsv = (value) => value
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);

  // Format address for AddressMapSelector (can be string or object)
  const formatAddressForSelector = (address) => {
    if (!address) return '';
    if (typeof address === 'string') return address;
    // If it's an object, combine street, city, state, postalCode
    const parts = [
      address.street,
      address.city,
      address.state,
      address.postalCode
    ].filter(Boolean);
    return parts.join(', ') || '';
  };

  const [formData, setFormData] = useState({
    businessName: supplier.businessName || '',
    category: supplier.category || '',
    contactName: supplier.contactName || '',
    phone: supplier.phone || '',
    email: supplier.user?.email || '',
    website: supplier.website || '',
    address: formatAddressForSelector(supplier.address),
    addressType: supplier.address?.type || (typeof supplier.address === 'object' && supplier.address ? 'business' : 'business'),
    location: supplier.location || null,
    status: supplier.status || 'pending',
    isVerified: supplier.isVerified ?? false,
    description: supplier.description || '',
    startingPrice: supplier.startingPrice ?? '',
    maxPrice: supplier.maxPrice ?? '',
    pricingModel: supplier.pricingModel || '',
    pricingNotes: supplier.pricingNotes || '',
    serviceAreas: toCsv(supplier.serviceAreas),
    weddingType: toCsv(supplier.weddingType),
    vibeTags: toCsv(supplier.vibeTags),
    formality: supplier.formality || '',
    instagram: supplier.socialMedia?.instagram || '',
    facebook: supplier.socialMedia?.facebook || '',
    tiktok: supplier.socialMedia?.tiktok || '',
    youtube: supplier.socialMedia?.youtube || '',
    cancellationPolicy: supplier.policies?.cancellation || '',
    depositPolicy: supplier.policies?.deposit || '',
    paymentPolicy: supplier.policies?.payment || '',
    refundPolicy: supplier.policies?.refund || ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [usersForReassign, setUsersForReassign] = useState([]);
  const [assignedUserId, setAssignedUserId] = useState(supplier.user?._id?.toString?.() || '');
  const [reassignLoading, setReassignLoading] = useState(false);
  const verificationStatus = getVerificationStatus({
    isVerified: formData.isVerified,
    status: formData.status
  });

  // Sync assigned user when supplier changes
  useEffect(() => {
    setAssignedUserId(supplier.user?._id?.toString?.() || '');
  }, [supplier.user?._id]);

  // Fetch users for reassign dropdown
  useEffect(() => {
    let cancelled = false;
    const fetchUsers = async () => {
      try {
        const res = await api.get('/api/admin/users');
        if (cancelled) return;
        const users = (res.data || []).filter(
          (u) => u._id && !['admin', 'support'].includes(u.role)
        );
        setUsersForReassign(users);
      } catch {
        if (!cancelled) setUsersForReassign([]);
      }
    };
    fetchUsers();
    return () => { cancelled = true; };
  }, []);

  // Update formData when supplier changes
  useEffect(() => {
    setFormData({
      businessName: supplier.businessName || '',
      category: supplier.category || '',
      contactName: supplier.contactName || '',
      phone: supplier.phone || '',
      email: supplier.user?.email || '',
      website: supplier.website || '',
      address: formatAddressForSelector(supplier.address),
      addressType: supplier.address?.type || (typeof supplier.address === 'object' && supplier.address ? 'business' : 'business'),
      location: supplier.location || null,
      status: supplier.status || 'pending',
      isVerified: supplier.isVerified ?? false,
      description: supplier.description || '',
      startingPrice: supplier.startingPrice ?? '',
      maxPrice: supplier.maxPrice ?? '',
      pricingModel: supplier.pricingModel || '',
      pricingNotes: supplier.pricingNotes || '',
      serviceAreas: toCsv(supplier.serviceAreas),
      weddingType: toCsv(supplier.weddingType),
      vibeTags: toCsv(supplier.vibeTags),
      formality: supplier.formality || '',
      instagram: supplier.socialMedia?.instagram || '',
      facebook: supplier.socialMedia?.facebook || '',
      tiktok: supplier.socialMedia?.tiktok || '',
      youtube: supplier.socialMedia?.youtube || '',
      cancellationPolicy: supplier.policies?.cancellation || '',
      depositPolicy: supplier.policies?.deposit || '',
      paymentPolicy: supplier.policies?.payment || '',
      refundPolicy: supplier.policies?.refund || ''
    });
  }, [supplier]);

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setError('');
    setSuccess('');
  };

  const handleAddressChange = (value) => {
    setFormData(prev => ({
      ...prev,
      address: value
    }));
    setError('');
    setSuccess('');
  };

  const handleAddressTypeChange = (addressType) => {
    setFormData(prev => ({
      ...prev,
      addressType: addressType
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        businessName: formData.businessName,
        category: formData.category,
        contactName: formData.contactName,
        phone: formData.phone,
        email: formData.email,
        website: formData.website,
        address: formData.address,
        addressType: formData.addressType,
        status: formData.status,
        isVerified: formData.isVerified,
        description: formData.description,
        startingPrice: formData.startingPrice,
        maxPrice: formData.maxPrice,
        pricingModel: formData.pricingModel,
        pricingNotes: formData.pricingNotes,
        serviceAreas: fromCsv(formData.serviceAreas),
        weddingType: fromCsv(formData.weddingType),
        vibeTags: fromCsv(formData.vibeTags),
        formality: formData.formality,
        socialMedia: {
          instagram: formData.instagram,
          facebook: formData.facebook,
          tiktok: formData.tiktok,
          youtube: formData.youtube
        },
        policies: {
          cancellation: formData.cancellationPolicy,
          deposit: formData.depositPolicy,
          payment: formData.paymentPolicy,
          refund: formData.refundPolicy
        }
      };

      await api.put(`/api/admin/supplier/${supplier._id}`, payload);
      setSuccess('Supplier profile updated successfully');
      onUpdate(); // Refresh the suppliers list
    } catch (error) {
      setError(error.response?.data?.message || 'Error updating supplier profile');
    } finally {
      setLoading(false);
    }
  };

  const handleReassignOwner = async () => {
    if (!supplier._id || !assignedUserId) return;
    const currentOwnerId = supplier.user?._id?.toString?.();
    if (assignedUserId === currentOwnerId) return;
    try {
      setReassignLoading(true);
      setError('');
      await api.put(`/api/admin/business/${supplier._id}/owner`, {
        newOwnerUserId: assignedUserId
      });
      setSuccess('Owner reassigned successfully');
      onUpdate();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reassign owner.');
    } finally {
      setReassignLoading(false);
    }
  };

  const currentOwnerId = supplier.user?._id?.toString?.();
  const hasOwnerChanged = assignedUserId && assignedUserId !== currentOwnerId;

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        {/* Header with Avatar and Status */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={supplier.logo}
                sx={{ width: 100, height: 100 }}
              >
                {supplier.businessName?.[0]}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6">{supplier.businessName}</Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={verificationStatus.label}
                    color={verificationStatus.color}
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <Chip
                    label={supplier.category}
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Assigned user - between profile area and business name field */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Autocomplete
              size="small"
              sx={{ minWidth: 280 }}
              options={usersForReassign}
              value={usersForReassign.find((u) => u._id?.toString?.() === assignedUserId) || null}
              onChange={(_, newValue) => setAssignedUserId(newValue?._id?.toString?.() || '')}
              getOptionLabel={(option) => {
                const name = [option.firstName, option.lastName].filter(Boolean).join(' ');
                return name ? `${option.email} (${name})` : option.email || '—';
              }}
              renderInput={(params) => (
                <TextField {...params} label="Assigned user" placeholder="Type to search…" />
              )}
            />
            {hasOwnerChanged && (
              <Button
                size="small"
                variant="contained"
                onClick={handleReassignOwner}
                disabled={reassignLoading}
              >
                {reassignLoading ? 'Updating…' : 'Update'}
              </Button>
            )}
          </Box>
        </Grid>

        {/* Alert Messages */}
        {error && (
          <Grid item xs={12}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        )}
        {success && (
          <Grid item xs={12}>
            <Alert severity="success">{success}</Alert>
          </Grid>
        )}

        {/* Form Fields */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Business Name"
            value={formData.businessName}
            onChange={handleChange('businessName')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={formData.category}
              onChange={handleChange('category')}
              label="Category"
            >
              {supplierCategories.map((category) => (
                <MenuItem key={category.value} value={category.value}>
                  {category.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              onChange={handleChange('status')}
              label="Status"
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Verified</InputLabel>
            <Select
              value={formData.isVerified ? 'verified' : 'not_verified'}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, isVerified: e.target.value === 'verified' }));
                setError('');
                setSuccess('');
              }}
              label="Verified"
            >
              <MenuItem value="verified">Verified</MenuItem>
              <MenuItem value="not_verified">Not Verified</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Contact Name"
            value={formData.contactName}
            onChange={handleChange('contactName')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Phone"
            value={formData.phone}
            onChange={handleChange('phone')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Email"
            value={formData.email}
            onChange={handleChange('email')}
            type="email"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Website"
            value={formData.website}
            onChange={handleChange('website')}
          />
        </Grid>
        <Grid item xs={12}>
          <AddressMapSelector
            value={formData.address}
            onChange={handleAddressChange}
            addressType={formData.addressType}
            onAddressTypeChange={handleAddressTypeChange}
            label="Address"
            placeholder="Enter business address..."
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={handleChange('description')}
            multiline
            rows={4}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Starting Price"
            type="number"
            value={formData.startingPrice}
            onChange={handleChange('startingPrice')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Max Price"
            type="number"
            value={formData.maxPrice}
            onChange={handleChange('maxPrice')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Pricing Model</InputLabel>
            <Select
              value={formData.pricingModel}
              onChange={handleChange('pricingModel')}
              label="Pricing Model"
            >
              <MenuItem value="">Not set</MenuItem>
              <MenuItem value="per_person">Per Person</MenuItem>
              <MenuItem value="flat_fee">Flat Fee</MenuItem>
              <MenuItem value="per_hour">Per Hour</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Formality"
            value={formData.formality}
            onChange={handleChange('formality')}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Pricing Notes"
            value={formData.pricingNotes}
            onChange={handleChange('pricingNotes')}
            multiline
            rows={2}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Service Areas (comma separated)"
            value={formData.serviceAreas}
            onChange={handleChange('serviceAreas')}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Wedding Types (comma separated)"
            value={formData.weddingType}
            onChange={handleChange('weddingType')}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Vibe Tags (comma separated)"
            value={formData.vibeTags}
            onChange={handleChange('vibeTags')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Instagram"
            value={formData.instagram}
            onChange={handleChange('instagram')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Facebook"
            value={formData.facebook}
            onChange={handleChange('facebook')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="TikTok"
            value={formData.tiktok}
            onChange={handleChange('tiktok')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="YouTube"
            value={formData.youtube}
            onChange={handleChange('youtube')}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Cancellation Policy"
            value={formData.cancellationPolicy}
            onChange={handleChange('cancellationPolicy')}
            multiline
            rows={2}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Deposit Policy"
            value={formData.depositPolicy}
            onChange={handleChange('depositPolicy')}
            multiline
            rows={2}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Payment Policy"
            value={formData.paymentPolicy}
            onChange={handleChange('paymentPolicy')}
            multiline
            rows={2}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Refund Policy"
            value={formData.refundPolicy}
            onChange={handleChange('refundPolicy')}
            multiline
            rows={2}
          />
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            fullWidth
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SupplierProfile; 